import { HttpApiService } from "@/shared/services/httpApi.service";
import type { AxiosResponse } from "axios";
import type {
  OrganizationsApiResponse,
  OrganizationsFilters,
  Organization,
} from "../types/organizations-api.types";
import type {
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from "../types/organization-form.types";
import { ORGANIZATIONS_API_ENDPOINTS } from "../constants/organizations.constants";

export class OrganizationsService {
  /**
   * Fetch organizations with optional filters
   * @param filters - Filter options including limit, skip, status, etc.
   * @note Uses limit/skip pagination (NestJS style) instead of page/per_page
   */
  static async fetchOrganizations(
    filters: OrganizationsFilters
  ): Promise<AxiosResponse<OrganizationsApiResponse>> {
    const { limit, skip, search, status, card_provider } = filters;

    const queryParams: string[] = [];

    if (limit !== undefined && limit !== null) {
      queryParams.push(`limit=${limit}`);
    }

    if (skip !== undefined && skip !== null) {
      queryParams.push(`skip=${skip}`);
    }

    if (search && search.trim() !== "") {
      queryParams.push(`search=${encodeURIComponent(search.trim())}`);
    }

    if (status) {
      queryParams.push(`status=${encodeURIComponent(status)}`);
    }

    if (card_provider) {
      queryParams.push(`card_provider=${encodeURIComponent(card_provider)}`);
    }

    let url = ORGANIZATIONS_API_ENDPOINTS.ORGANIZATIONS;
    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    return HttpApiService.get<OrganizationsApiResponse>(url);
  }

  /**
   * Create a new organization
   * @param data - Organization data to create
   */
  static async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<AxiosResponse<Organization>> {
    return HttpApiService.post<Organization>(ORGANIZATIONS_API_ENDPOINTS.ORGANIZATIONS, data);
  }

  /**
   * Get a specific organization by ID
   * @param id - Organization UUID
   */
  static async getOrganizationById(
    id: string
  ): Promise<AxiosResponse<Organization>> {
    return HttpApiService.get<Organization>(
      `${ORGANIZATIONS_API_ENDPOINTS.ORGANIZATIONS}/${id}`
    );
  }

  /**
   * Update an organization
   * @param id - Organization UUID
   * @param data - Organization data to update
   */
  static async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<AxiosResponse<Organization>> {
    return HttpApiService.patch<Organization>(
      `${ORGANIZATIONS_API_ENDPOINTS.ORGANIZATIONS}/${id}`,
      data
    );
  }

  /**
   * Delete an organization
   * @param id - Organization UUID
   */
  static async deleteOrganization(
    id: string
  ): Promise<AxiosResponse<void>> {
    return HttpApiService.delete<void>(
      `${ORGANIZATIONS_API_ENDPOINTS.ORGANIZATIONS}/${id}`
    );
  }
}
